import { Box, Flex, Text } from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import ClientSideTable from "../components/table/ClientSideTable";
import {
	type ColumnDef,
	type ColumnFiltersState,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import type { CommandExport } from "../types/command_export.types";
import { useCallback, useMemo, useState } from "react";
import { format } from "date-fns";
import { useCommandExports } from "../hooks/command_exports.hooks";
import DownloadIcon from "../components/icons/DownloadIcon";
import PrimaryButton from "../components/PrimaryButton";
import { AddIcon } from "@chakra-ui/icons";
import { useMutation, useQueryClient } from "react-query";
import {
	createNewExport,
	retrieveExport,
} from "../mutations/command.mutations";
import { useSimpleToast } from "../hooks/toast.hooks";
import _ from "lodash";
import InlineUserAvatar from "../components/InlineUserAvatar";
import InputSingleDatePicker from "../components/inputs/InputSingleDatePicker";
import DocumentIcon from "../components/icons/DocumentIcon";
import CalendarIcon from "../components/icons/CalendarIcon";

function OrdersExportsPage() {
	const toast = useSimpleToast();
	const queryClient = useQueryClient();
	const { mutate: mutateCreateNewExport } = useMutation(createNewExport, {
		onSuccess: () => {
			toast.success("Export", "L'export a été créé avec succès");
			queryClient.invalidateQueries(["commands", "exports"]);
		},
	});

	const { mutate: mutateRetrieveExport } = useMutation(retrieveExport, {
		onSuccess: (data: { content: string }) => {
			const element = document.createElement("a");
			element.setAttribute(
				"href",
				"data:text/plain;charset=utf-8," + encodeURIComponent(data.content),
			);
			element.setAttribute("download", format(new Date(), "ddMMyyyy"));
			element.style.display = "none";
			document.body.appendChild(element);
			element.click();
			document.body.removeChild(element);
		},
	});

	const handleNewExport = useCallback(() => {
		mutateCreateNewExport();
	}, [mutateCreateNewExport]);

	const handleDownload = useCallback(
		(id: number) => {
			mutateRetrieveExport(id);
		},
		[mutateRetrieveExport],
	);

	function OrdersExportsNoDataComponent() {
		return (
			<Flex w="100%" justifyContent="center">
				<Flex
					w="100%"
					rounded="xl"
					bg="white"
					textAlign="center"
					lineHeight="44px"
					color="gray.500"
					direction={"column"}
					justifyContent={"center"}
					alignItems={"center"}
					py="100px"
				>
					<Flex
						w="48px"
						h="48px"
						bg="zinc.50"
						border="solid 1px"
						borderColor="brand.100"
						justifyContent="center"
						alignItems="center"
						borderRadius={100}
						mr="8px"
					>
						<DocumentIcon w="24px" h="24px" color="brand.700" />
					</Flex>
					<Text
						mt={4}
						color={"gray.900"}
						fontSize={"20px"}
						lineHeight={"30px"}
						fontWeight={600}
					>
						Aucun export
					</Text>
					<PrimaryButton mt={4} mb={4} w={"30rem"} onClick={handleNewExport}>
						Créer un export
					</PrimaryButton>
				</Flex>
			</Flex>
		);
	}

	const columns = useMemo<ColumnDef<CommandExport>[]>(
		() => [
			{
				accessorFn: (row) => row.exportNumber,
				id: "exportNumber",
				header: "N° export",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => format(row.createdAt, "dd/MM/yyyy"),
				id: "exportDate",
				header: "Date d'export",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<Flex h="44px" alignItems="center">
						<InlineUserAvatar id={row.creatorId} />
					</Flex>
				),
				id: "creator",
				header: "Créé par",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<Box mr={4}>
						<DownloadIcon
							cursor={"pointer"}
							onClick={() => handleDownload(row.id)}
							color="gray.500"
						></DownloadIcon>
					</Box>
				),
				id: "more",
				header: "",
				cell: (info) => info.getValue(),
			},
		],
		[],
	);
	const data = useCommandExports();
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const table = useReactTable({
		data: data.commands ?? [],
		columns: columns,
		state: {
			columnFilters,
		},
		globalFilterFn: (row, _columnId, value) => {
			const lowerValue = new String(value).toLowerCase();
			const checks = [
				row.original.creator.firstName.toLowerCase().includes(lowerValue),
				row.original.creator.lastName.toLowerCase().includes(lowerValue),
				row.original.exportNumber.toLowerCase().includes(lowerValue),
				format(row.original.createdAt, "dd/MM/yyyy").includes(lowerValue),
			];
			return _.some(checks, (check) => check);
		},
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
	});

	function OrdersExportsActionsComponent() {
		const [date, setDate] = useState<Date | undefined>(undefined);
		return (
			<Flex gap={4}>
				<Box width="20rem">
					<InputSingleDatePicker
						placeholder={
							<>
								<CalendarIcon mr={2} />
								Sélectionnez une date
							</>
						}
						date={date}
						setDate={(date?: Date) => {
							if (date === undefined) {
								return;
							}
							setDate(date);
							const exportDateColumn = table.getColumn("exportDate");
							if (exportDateColumn) {
								exportDateColumn.setFilterValue(() => {
									return format(date, "dd/MM/yyyy");
								});
							}
						}}
					/>
				</Box>
				<PrimaryButton onClick={handleNewExport} w="fit-content">
					<AddIcon mr={4}></AddIcon>Créer un export
				</PrimaryButton>
			</Flex>
		);
	}

	return (
		<>
			<PageTitle>Exports</PageTitle>
			<Box w="100%">
				<ClientSideTable
					table={table}
					w={["8rem", "10rem", "10rem", "auto"]}
					flexGrow={[0, 0, 1, 0]}
					actionsComponent={OrdersExportsActionsComponent}
					noDataComponent={OrdersExportsNoDataComponent}
				></ClientSideTable>
			</Box>
		</>
	);
}

export default OrdersExportsPage;
